import { useQuery } from "@tanstack/react-query"

import { supabase } from "@/lib/supabase"

/**
 * The response shape from the get-session-dates edge function.
 *
 * Keep in sync with AvailableDate in supabase/functions/get-session-dates/index.ts
 *
 * One intentional difference: `date` is an ISO string on the wire and a Date
 * here, which fetchSessionDates converts on receipt.
 */
export interface AvailableDate {
  date: Date
  sessionId: string
  startTime: string
  endTime: string
  depot?: {
    name: string
    address: string
    city: string
  }
  locationAddress?: string
}

/**
 * Function to call the Supabase Edge Function for session dates
 */
async function fetchSessionDates(
  jobId: string,
  sessionType: "orientation" | "vehicle_collection",
): Promise<AvailableDate[]> {
  try {
    // Call the Supabase Edge Function
    const { data, error } = await supabase.functions.invoke("get-session-dates", {
      body: { jobId, sessionType },
    })

    if (error) {
      console.error("Error calling get-session-dates function:", error)
      throw new Error("Failed to fetch available sessions")
    }

    // The Edge Function returns data in the format { data: AvailableDate[] }
    return (
      data?.data.map((d) => ({
        ...d,
        date: new Date(d.date),
      })) || []
    )
  } catch (error) {
    console.error("Error in fetchSessionDates:", error)
    throw error
  }
}

/**
 * Hook to fetch available session dates for a specific job and session type
 * Uses a Supabase Edge Function to handle the complex logic server-side
 */
export const useSessionDates = (
  jobId: string | undefined,
  sessionType: "orientation" | "vehicle_collection" = "orientation",
) => {
  return useQuery({
    queryKey: ["sessionDates", jobId, sessionType],
    queryFn: async (): Promise<AvailableDate[]> => {
      if (!jobId) {
        return []
      }

      return fetchSessionDates(jobId, sessionType)
    },
    enabled: !!jobId,
  })
}
